import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, ChevronRight, Globe } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/hooks/useLanguage";
import TopBar from "@/components/landing/TopBar";

const Header = () => {
  const { t, language, setLanguage, localizePath } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMobileSection, setExpandedMobileSection] = useState<string | null>(null);
  const [openDesktopMenu, setOpenDesktopMenu] = useState<string | null>(null);
  const desktopNavRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!openDesktopMenu) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (desktopNavRef.current && !desktopNavRef.current.contains(event.target as Node)) {
        setOpenDesktopMenu(null);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [openDesktopMenu]);

  const toggleMobileSection = (section: string) => {
    setExpandedMobileSection(expandedMobileSection === section ? null : section);
  };

  const toggleDesktopMenu = (section: string) => {
    // Always open on click. Closing is handled by outside-click / mouse-leave,
    // and re-clicking the same trigger keeps it open (no flicker when hover
    // set the same value just before the click).
    setOpenDesktopMenu(section);
  };

  const topNavData = {
    ressourcen: {
      label: t('header.topNav.ressourcen.label'),
      items: [
        { label: t('header.topNav.ressourcen.items.blog'), href: localizePath('/blog') },
        { label: t('header.topNav.ressourcen.items.faqs'), href: localizePath('/faq') },
      ],
    },
  };

  const navigationData = {
    produkte: {
      label: t('header.nav.products.label'),
      sections: [
        {
          title: t('header.nav.products.sections.products'),
          items: [
            { label: t('header.nav.products.items.aiAgentPlatform.label'), href: localizePath('/produkte/ai-agent-plattform'), description: t('header.nav.products.items.aiAgentPlatform.description') },
            { label: t('header.nav.products.items.aiAgent.label'), href: localizePath('/produkte/ai-agent'), description: t('header.nav.products.items.aiAgent.description') },
            { label: t('header.nav.products.items.knowledgeBase.label'), href: localizePath('/produkte/ai-knowledge-base'), description: t('header.nav.products.items.knowledgeBase.description') },
            { label: t('header.nav.products.items.workflows.label'), href: localizePath('/produkte/ai-automation-workflows'), description: t('header.nav.products.items.workflows.description') },
            { label: t('header.nav.products.items.inbox.label'), href: localizePath('/produkte/unified-inbox'), description: t('header.nav.products.items.inbox.description') },
            { label: t('header.nav.products.items.phonebot.label'), href: '#phonebot', description: t('header.nav.products.items.phonebot.description'), comingSoon: true },
            { label: t('header.nav.products.items.analytics.label'), href: localizePath('/produkte/analytics'), description: t('header.nav.products.items.analytics.description') },
            { label: 'Enterprise Hosting', href: localizePath('/produkte/enterprise-hosting'), description: language === 'en' ? 'On-premise & private cloud for full data control' : 'On-Premise & Private Cloud für volle Datenkontrolle', separator: true },
          ],
        },
        {
          title: t('header.nav.products.sections.integrations'),
          items: [
            { label: 'HubSpot', href: `${localizePath('/integrationen')}#hubspot` },
            { label: 'WhatsApp', href: `${localizePath('/integrationen')}#whatsapp` },
            { label: 'Shopify', href: `${localizePath('/integrationen')}#shopify` },
            { label: 'Pipedrive', href: `${localizePath('/integrationen')}#pipedrive` },
            { label: 'TYPO3', href: `${localizePath('/integrationen')}#typo3` },
          ],
          showMoreLink: localizePath('/integrationen'),
        },
      ],
    },
    loesungen: {
      label: t('header.nav.solutions.label'),
      sections: [
        {
          title: t('header.nav.solutions.sections.solutions'),
          items: [
            { label: t('header.nav.solutions.items.marketing.label'), href: localizePath('/loesungen/marketing'), description: t('header.nav.solutions.items.marketing.description') },
            { label: t('header.nav.solutions.items.sales.label'), href: localizePath('/loesungen/vertrieb'), description: t('header.nav.solutions.items.sales.description') },
            { label: t('header.nav.solutions.items.service.label'), href: localizePath('/loesungen/kundenservice'), description: t('header.nav.solutions.items.service.description') },
            { label: t('header.nav.solutions.items.ecommerce.label'), href: localizePath('/loesungen/ecommerce'), description: t('header.nav.solutions.items.ecommerce.description') },
          ],
        },
        {
          title: t('header.nav.solutions.sections.industries'),
          items: [
            { label: t('header.nav.solutions.industries.manufacturing'), href: localizePath('/branchen/maschinenbau') },
            { label: t('header.nav.solutions.industries.associations'), href: localizePath('/branchen/verbaende') },
            { label: t('header.nav.solutions.industries.itSoftware'), href: localizePath('/branchen/it-software') },
            { label: t('header.nav.solutions.industries.retail'), href: localizePath('/branchen/einzelhandel') },
            { label: t('header.nav.solutions.industries.ecommerceIndustry'), href: localizePath('/branchen/e-commerce') },
            { label: t('header.nav.solutions.industries.tourism'), href: localizePath('/branchen/tourismus') },
            { label: t('header.nav.solutions.industries.insurance'), href: localizePath('/branchen/versicherung') },
          ],
        },
      ],
    },
    caseStudies: {
      label: t('header.nav.caseStudies.label'),
      sections: [
        {
          title: t('header.nav.caseStudies.sections.successStories'),
          items: [
            { label: t('header.nav.caseStudies.items.support.label'), href: localizePath('/case-studies/verbaende/customer-support'), description: t('header.nav.caseStudies.items.support.description') },
            { label: t('header.nav.caseStudies.items.sales.label'), href: localizePath('/case-studies/vertrieb'), description: t('header.nav.caseStudies.items.sales.description') },
            { label: t('header.nav.caseStudies.items.marketing.label'), href: localizePath('/case-studies/marketing'), description: t('header.nav.caseStudies.items.marketing.description') },
            { label: t('header.nav.caseStudies.items.ecommerce.label'), href: localizePath('/case-studies/online-shops-e-commerce/big-green-egg-head'), description: t('header.nav.caseStudies.items.ecommerce.description') },
          ],
        },
      ],
    },
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <TopBar />
      <div className="bg-background/95 backdrop-blur-sm">
        <div className="hidden lg:block border-b border-border bg-secondary/30">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-end h-9 gap-6">
              <div className="relative group">
                <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary transition-colors py-2">
                  {topNavData.ressourcen.label}
                  <ChevronDown className="w-3 h-3" />
                </button>
                <div className="absolute right-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                  <div className="bg-background border border-border rounded-lg shadow-lg py-2 min-w-[160px]">
                    {topNavData.ressourcen.items.map((item) => (
                      <Link key={item.label} to={item.href} className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-secondary hover:text-primary transition-colors">
                        {item.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              <Link to={localizePath('/solution-partner')} className="text-sm text-muted-foreground hover:text-primary transition-colors">
                {t('header.topNav.solutionProgram')}
              </Link>

              <div className="relative group">
                <button className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                  {t('header.topNav.aboutUs.label')}
                  <svg className="w-3 h-3 transition-transform group-hover:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                <div className="absolute top-full left-0 mt-2 bg-background rounded-lg shadow-lg border border-border py-2 min-w-[160px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <Link to={localizePath('/ueber-uns')} className="block px-4 py-2 text-sm text-foreground hover:text-primary hover:bg-muted transition-colors">{t('header.topNav.aboutUs.items.company')}</Link>
                  <Link to={localizePath('/karriere')} className="block px-4 py-2 text-sm text-foreground hover:text-primary hover:bg-muted transition-colors">{t('header.topNav.aboutUs.items.career')}</Link>
                </div>
              </div>

              <a href="https://app.chathero.ai/login" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:text-chathero-mint transition-colors">
                {t('header.topNav.login')}
              </a>

              <LanguageSwitcher variant="icon" />
            </div>
          </div>
        </div>

        <div className="border-b border-border">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between h-16 md:h-20">
              <Link to={localizePath('/')} className="flex items-center flex-shrink-0">
                <img src="/assets/logo_chathero.png" alt="Chathero Logo" className="h-12 w-auto" />
              </Link>

              <div ref={desktopNavRef} className="hidden lg:flex items-center gap-1">
                <div
                  className="relative"
                  onMouseEnter={() => setOpenDesktopMenu('produkte')}
                  onMouseLeave={() => setOpenDesktopMenu((current) => current === 'produkte' ? null : current)}
                >
                  <button
                    type="button"
                    onClick={() => toggleDesktopMenu('produkte')}
                    aria-expanded={openDesktopMenu === 'produkte'}
                    className="inline-flex h-10 items-center justify-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {navigationData.produkte.label}
                    <ChevronDown className={cn("h-3 w-3 transition-transform", openDesktopMenu === 'produkte' && "rotate-180")} />
                  </button>

                  {openDesktopMenu === 'produkte' && (
                    <div className="absolute left-1/2 top-full z-50 w-[700px] -translate-x-1/2 pt-2">
                      <div className="rounded-lg border border-border bg-background p-6 shadow-xl">
                        <div className="grid grid-cols-2 gap-8">
                          {navigationData.produkte.sections.map((section) => (
                            <div key={section.title}>
                              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary">{section.title}</h3>
                              <ul className="space-y-2">
                                {section.items.map((item) => (
                                  <li key={item.label}>
                                    <Link
                                      to={item.href}
                                      onClick={() => setOpenDesktopMenu(null)}
                                      className={cn("block rounded-lg p-2 transition-colors hover:bg-secondary group", item.comingSoon && "opacity-60", item.separator && "mt-2 border-t border-border pt-2")}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-foreground transition-colors group-hover:text-primary">{item.label}</span>
                                        {item.comingSoon && <span className="rounded-full bg-chathero-mint/20 px-2 py-0.5 text-xs text-chathero-mint">{t('common.comingSoon')}</span>}
                                      </div>
                                      {item.description && <p className="mt-0.5 text-sm text-muted-foreground">{item.description}</p>}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                              {section.showMoreLink && (
                                <Link
                                  to={section.showMoreLink}
                                  onClick={() => setOpenDesktopMenu(null)}
                                  className="mt-3 flex items-center gap-1 px-2 text-sm font-medium text-primary transition-colors hover:text-chathero-mint"
                                >
                                  {t('header.nav.products.sections.allIntegrations')}
                                  <ChevronRight className="h-3.5 w-3.5" />
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className="relative"
                  onMouseEnter={() => setOpenDesktopMenu('loesungen')}
                  onMouseLeave={() => setOpenDesktopMenu((current) => current === 'loesungen' ? null : current)}
                >
                  <button
                    type="button"
                    onClick={() => toggleDesktopMenu('loesungen')}
                    aria-expanded={openDesktopMenu === 'loesungen'}
                    className="inline-flex h-10 items-center justify-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {navigationData.loesungen.label}
                    <ChevronDown className={cn("h-3 w-3 transition-transform", openDesktopMenu === 'loesungen' && "rotate-180")} />
                  </button>

                  {openDesktopMenu === 'loesungen' && (
                    <div className="absolute left-1/2 top-full z-50 w-[600px] -translate-x-1/2 pt-2">
                      <div className="rounded-lg border border-border bg-background p-6 shadow-xl">
                        <div className="grid grid-cols-2 gap-8">
                          {navigationData.loesungen.sections.map((section) => (
                            <div key={section.title}>
                              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary">{section.title}</h3>
                              <ul className="space-y-2">
                                {section.items.map((item) => (
                                  <li key={item.label}>
                                    <Link
                                      to={item.href}
                                      onClick={() => setOpenDesktopMenu(null)}
                                      className="block rounded-lg p-2 transition-colors hover:bg-secondary group"
                                    >
                                      <span className="font-medium text-foreground transition-colors group-hover:text-primary">{item.label}</span>
                                      {item.description && <p className="mt-0.5 text-sm text-muted-foreground">{item.description}</p>}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className="relative"
                  onMouseEnter={() => setOpenDesktopMenu('caseStudies')}
                  onMouseLeave={() => setOpenDesktopMenu((current) => current === 'caseStudies' ? null : current)}
                >
                  <button
                    type="button"
                    onClick={() => toggleDesktopMenu('caseStudies')}
                    aria-expanded={openDesktopMenu === 'caseStudies'}
                    className="inline-flex h-10 items-center justify-center gap-1 rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {navigationData.caseStudies.label}
                    <ChevronDown className={cn("h-3 w-3 transition-transform", openDesktopMenu === 'caseStudies' && "rotate-180")} />
                  </button>

                  {openDesktopMenu === 'caseStudies' && (
                    <div className="absolute left-1/2 top-full z-50 w-[400px] -translate-x-1/2 pt-2">
                      <div className="rounded-lg border border-border bg-background p-6 shadow-xl">
                        {navigationData.caseStudies.sections.map((section) => (
                          <div key={section.title}>
                            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-primary">{section.title}</h3>
                            <ul className="space-y-2">
                              {section.items.map((item) => (
                                <li key={item.label}>
                                  <Link
                                    to={item.href}
                                    onClick={() => setOpenDesktopMenu(null)}
                                    className="block rounded-lg p-3 transition-colors hover:bg-secondary group"
                                  >
                                    <span className="font-medium text-foreground transition-colors group-hover:text-primary">{item.label}</span>
                                    {item.description && <p className="mt-0.5 text-sm text-muted-foreground">{item.description}</p>}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Link to={localizePath('/preise')} className="px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-primary">
                  {t('header.nav.pricing')}
                </Link>
              </div>

              <div className="hidden lg:flex items-center gap-3">
                <Button variant="outline" className="border-chathero-orange text-chathero-orange hover:bg-chathero-orange hover:text-primary-foreground font-semibold animate-pulse" asChild>
                  <a href="https://app.chathero.ai/register/essential" target="_blank" rel="noopener noreferrer">{t('header.cta.trial')}</a>
                </Button>
                <Button className="bg-chathero-mint text-accent-foreground hover:bg-chathero-mint/90" asChild>
                  <Link to={localizePath('/kontakt')}>{t('header.cta.demo')}</Link>
                </Button>
              </div>

              <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label={t('header.mobileMenu')}>
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>

            {mobileMenuOpen && (
              <div className="lg:hidden py-4 border-t border-border animate-fade-in max-h-[calc(100vh-5rem)] overflow-y-auto">
                <nav className="flex flex-col gap-2">
                  <div className="border-b border-border pb-2">
                    <button onClick={() => toggleMobileSection('produkte')} className="flex items-center justify-between w-full py-3 px-2 text-left font-medium text-foreground">
                      <span>{navigationData.produkte.label}</span>
                      <ChevronDown className={cn('w-5 h-5 transition-transform', expandedMobileSection === 'produkte' && 'rotate-180')} />
                    </button>
                    {expandedMobileSection === 'produkte' && (
                      <div className="pl-4 pb-2 space-y-4">
                        {navigationData.produkte.sections.map((section) => (
                          <div key={section.title}>
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{section.title}</h4>
                            <ul className="space-y-1">
                              {section.items.map((item) => (
                                <li key={item.label}>
                                  <Link to={item.href} className="flex items-center gap-2 py-2 px-2 text-sm text-foreground hover:text-primary rounded-lg hover:bg-secondary" onClick={() => setMobileMenuOpen(false)}>
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                    {item.label}
                                    {item.comingSoon && <span className="text-xs bg-chathero-mint/20 text-chathero-mint px-1.5 py-0.5 rounded-full">soon</span>}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                            {section.showMoreLink && (
                              <Link to={section.showMoreLink} className="flex items-center gap-1 py-2 px-2 text-sm font-medium text-primary hover:text-chathero-mint transition-colors" onClick={() => setMobileMenuOpen(false)}>
                                {t('header.nav.products.sections.allIntegrations')}
                                <ChevronRight className="w-3.5 h-3.5" />
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="border-b border-border pb-2">
                    <button onClick={() => toggleMobileSection('loesungen')} className="flex items-center justify-between w-full py-3 px-2 text-left font-medium text-foreground">
                      <span>{navigationData.loesungen.label}</span>
                      <ChevronDown className={cn('w-5 h-5 transition-transform', expandedMobileSection === 'loesungen' && 'rotate-180')} />
                    </button>
                    {expandedMobileSection === 'loesungen' && (
                      <div className="pl-4 pb-2 space-y-4">
                        {navigationData.loesungen.sections.map((section) => (
                          <div key={section.title}>
                            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{section.title}</h4>
                            <ul className="space-y-1">
                              {section.items.map((item) => (
                                <li key={item.label}>
                                  <Link to={item.href} className="flex items-center gap-2 py-2 px-2 text-sm text-foreground hover:text-primary rounded-lg hover:bg-secondary" onClick={() => setMobileMenuOpen(false)}>
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                    {item.label}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="border-b border-border pb-2">
                    <button onClick={() => toggleMobileSection('caseStudies')} className="flex items-center justify-between w-full py-3 px-2 text-left font-medium text-foreground">
                      <span>{navigationData.caseStudies.label}</span>
                      <ChevronDown className={cn('w-5 h-5 transition-transform', expandedMobileSection === 'caseStudies' && 'rotate-180')} />
                    </button>
                    {expandedMobileSection === 'caseStudies' && (
                      <div className="pl-4 pb-2">
                        <ul className="space-y-1">
                          {navigationData.caseStudies.sections[0].items.map((item) => (
                            <li key={item.label}>
                              <Link to={item.href} className="flex items-center gap-2 py-2 px-2 text-sm text-foreground hover:text-primary rounded-lg hover:bg-secondary" onClick={() => setMobileMenuOpen(false)}>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                {item.label}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <Link to={localizePath('/preise')} className="py-3 px-2 font-medium text-foreground hover:text-primary border-b border-border" onClick={() => setMobileMenuOpen(false)}>
                    {t('header.nav.pricing')}
                  </Link>

                  <div className="py-2 border-b border-border">
                    <p className="px-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{topNavData.ressourcen.label}</p>
                    {topNavData.ressourcen.items.map((item) => (
                      <Link key={item.label} to={item.href} className="flex items-center gap-2 py-2 px-2 text-sm text-foreground hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        {item.label}
                      </Link>
                    ))}
                  </div>

                  <Link to={localizePath('/solution-partner')} className="py-3 px-2 text-sm text-foreground hover:text-primary border-b border-border" onClick={() => setMobileMenuOpen(false)}>
                    {t('header.topNav.solutionProgram')}
                  </Link>
                  <Link to={localizePath('/ueber-uns')} className="py-3 px-2 text-sm text-foreground hover:text-primary border-b border-border" onClick={() => setMobileMenuOpen(false)}>
                    {t('header.topNav.aboutUs.items.company')}
                  </Link>
                  <Link to={localizePath('/karriere')} className="py-3 px-2 text-sm text-foreground hover:text-primary border-b border-border" onClick={() => setMobileMenuOpen(false)}>
                    {t('header.topNav.aboutUs.items.career')}
                  </Link>

                  <div className="flex items-center gap-2 py-3 px-2 border-b border-border">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    {(['de', 'en'] as const).map((lang) => (
                      <button key={lang} onClick={() => { setLanguage(lang); setMobileMenuOpen(false); }} className={cn('px-3 py-1.5 text-sm rounded-md transition-colors', language === lang ? 'bg-primary text-primary-foreground font-medium' : 'text-muted-foreground hover:text-foreground')}>
                        {lang.toUpperCase()}
                      </button>
                    ))}
                  </div>

                  <div className="flex flex-col gap-2 pt-4 mt-2">
                    <Button variant="outline" className="w-full" asChild>
                      <a href="https://app.chathero.ai/login" target="_blank" rel="noopener noreferrer">{t('header.topNav.login')}</a>
                    </Button>
                    <Button className="w-full bg-chathero-mint text-accent-foreground" asChild>
                      <Link to={localizePath('/kontakt')}>{t('header.cta.demo')}</Link>
                    </Button>
                  </div>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
